import { useEffect, useState } from 'react'
import { houseAccountsAPI } from '../services/api'
import type { HouseAccount } from '../types'
import { apiErrorMessage } from '../utils/apiError'

const NAMES: Record<string, string> = {
  twitter: 'X (Twitter)',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  medium: 'Medium',
  telegram: 'Telegram',
}

/**
 * Admin only: FinCreator's own accounts that Publish (Library) and the
 * schedule post to. An account posts only when it's active, set to
 * auto-publish, and has keys stored; this says which are missing.
 */
const HouseAccountsPanel = () => {
  const [accounts, setAccounts] = useState<HouseAccount[] | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    houseAccountsAPI
      .list()
      .then(setAccounts)
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load the accounts.")))
  }, [])

  const toggle = async (account: HouseAccount, field: 'is_active' | 'auto_publish') => {
    setBusy(account.id)
    setError(null)
    try {
      const updated = await houseAccountsAPI.update(account.id, { [field]: !account[field] })
      setAccounts((prev) => prev?.map((a) => (a.id === updated.id ? updated : a)) ?? null)
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't change it."))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="mt-6 rounded-lg bg-white p-4 shadow" aria-label="FinCreator's accounts">
      <h2 className="text-lg font-semibold text-gray-900">FinCreator&rsquo;s accounts</h2>
      <p className="mt-1 text-sm text-gray-600">
        Where Publish and scheduled posts go. Creators post on their own accounts with &ldquo;Post it yourself&rdquo;.
      </p>
      {accounts && accounts.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">No accounts are set up on this server.</p>
      )}
      {accounts && accounts.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 text-sm" aria-label="Accounts">
          {accounts.map((a) => {
            const posts = a.is_active && a.auto_publish && a.has_credentials && !a.blocked_reason
            return (
              <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2" data-testid={`account-${a.platform_type}`}>
                <span className="w-28 font-medium text-gray-900">{NAMES[a.platform_type] ?? a.platform_type}</span>
                <label className="flex items-center gap-1 text-gray-700">
                  <input
                    type="checkbox"
                    checked={a.is_active}
                    disabled={busy === a.id}
                    onChange={() => toggle(a, 'is_active')}
                  />
                  Active
                </label>
                <label className="flex items-center gap-1 text-gray-700">
                  <input
                    type="checkbox"
                    checked={a.auto_publish}
                    disabled={busy === a.id}
                    onChange={() => toggle(a, 'auto_publish')}
                  />
                  Publish to it
                </label>
                <span className={`text-xs ${posts ? 'text-success-700' : 'text-gray-500'}`}>
                  {posts
                    ? `Posting · ${a.posts_today} of ${a.max_posts_per_day} today`
                    : !a.has_credentials
                      ? 'No keys stored: nothing can be posted'
                      : 'Not posting'}
                </span>
                {a.blocked_reason && (
                  <p className="basis-full text-xs text-amber-800" data-testid="account-blocked">
                    {a.blocked_reason}
                  </p>
                )}
                {a.privacy_level === 'SELF_ONLY' && !a.blocked_reason && (
                  <p className="basis-full text-xs text-amber-800">Posts go out private: only the account can see them.</p>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}
    </section>
  )
}

export default HouseAccountsPanel
